'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'

import Link from '@/components/link'
import Wallet from '@/utils/wallet'

import UserIconStatic from '@/components/user-icon-static'
import Address from '@/components/address'
import { CDN_URL } from '@/utils/constants'
import { LogOut, PenLine, Settings, Wallet as WalletIcon } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { STORAGE_PROFILE } from '@/utils/constants'
import event from '@/utils/event'
import useWindowSize from '@/utils/hooks/useWindowSize'
import { useLogout } from '@/utils/hooks/useLoggedIn'
import abbreviateAddress from '@/utils/abbreviate-address'
import useViewerProfile from '@/app/alkanes/lib/use-viewer-profile'
import { resolveTwetchImage } from '@/app/alkanes/lib/twetch-media'
import { openProfileEditor } from '@/app/alkanes/lib/twetch-ui'
import TwetchProfileHost from '@/app/alkanes/components/twetch-profile-host'

let INITIAL_RENDER = true

/// Same menu as the /alkanes account dropdown.
const MENU_ITEM =
	'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors hover:bg-secondary/60'

const HeaderProfile: FC<{
	onClick?: () => void
	className?: string
	showCharacterCount?: number
}> = (props): ReactElement => {
	const { width } = useWindowSize()
	const logout = useLogout()
	const viewer = useViewerProfile()
	let icon: any = null
	let sns: any = null

	const handleSignOut = (evt: any) => {
		evt.preventDefault()
		evt.stopPropagation()

		if (Wallet.type() === 'ordinalswallet') {
			return event.emit('open-signout-modal')
		}

		logout()
	}

	try {
		let profile = localStorage.getItem(STORAGE_PROFILE)
		const parsed = JSON.parse(profile as any)
		icon = parsed.icon
		sns = parsed.sns
	} catch (_e) {}

	React.useEffect(() => {
		let profile = localStorage.getItem(STORAGE_PROFILE)
		if (!profile && INITIAL_RENDER && Wallet.getTaprootAddress()) {
			INITIAL_RENDER = false
			Wallet.fetchProfile()
		}
	}, [])

	const address = Wallet.getPublicKey()
	const gradientId = parseInt(address, 16)
	const taproot = Wallet.getTaprootAddress() || ''
	// The profile the wallet set (its Twetch name and picture) is who they are
	// everywhere; the legacy OW icon / SNS name is the fallback.
	const iconSrc = resolveTwetchImage(viewer.icon) ?? (icon ? `${CDN_URL}/${icon}` : undefined)
	const displayName = viewer.name?.trim() || sns
	// Hover opens it (as on /alkanes); the short close delay lets the pointer
	// cross the gap between the trigger and the panel.
	const [menuOpen, setMenuOpen] = React.useState(false)
	const closeTimer = React.useRef<number | null>(null)
	const clearCloseTimer = () => {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current)
			closeTimer.current = null
		}
	}
	const openMenu = () => {
		clearCloseTimer()
		setMenuOpen(true)
	}
	const scheduleClose = () => {
		clearCloseTimer()
		closeTimer.current = window.setTimeout(() => setMenuOpen(false), 150)
	}
	const closeMenu = () => {
		clearCloseTimer()
		setMenuOpen(false)
	}
	React.useEffect(() => clearCloseTimer, [])

	const trigger = (
		<Link
			href={'/wallet'}
			onClick={props?.onClick}
			className={classNames('flex items-center gap-1 !important')}
		>
			<div className="size-1 bg-[#00CC5E]" />
			<Address
				className="text-foreground text-xs hover:opacity-80 h-10 flex items-center"
				value={displayName as any}
				showCharacterCount={viewer.name ? undefined : props?.showCharacterCount}
			/>
			<UserIconStatic src={iconSrc} defaultGradientId={gradientId} size={40} />
		</Link>
	)

	// Phones get the plain link; the menu is a pointer affordance.
	if (!((width as any) >= 769)) {
		return (
			<>
				<TwetchProfileHost />
				{trigger}
			</>
		)
	}

	return (
		<>
			<TwetchProfileHost />
			<Popover open={menuOpen} onOpenChange={(open) => !open && closeMenu()}>
				<PopoverTrigger asChild>
					<div onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
						{trigger}
					</div>
				</PopoverTrigger>
				<PopoverContent
					align="end"
					sideOffset={6}
					role="menu"
					data-testid="header-profile-menu"
					className="w-60 p-1.5"
					onMouseEnter={openMenu}
					onMouseLeave={scheduleClose}
					// A hover-open must not yank keyboard focus into the panel.
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<div className="flex items-center gap-2.5 px-3 py-2">
						<UserIconStatic src={iconSrc} defaultGradientId={gradientId} size={36} />
						<div className="min-w-0 flex-1">
							<div className="truncate text-sm font-semibold leading-5">
								{displayName || abbreviateAddress(taproot)}
							</div>
							<div className="flex items-center gap-1.5 text-xs leading-4 text-muted-foreground">
								<span className="size-1.5 shrink-0 rounded-full bg-[#00CC5E]" aria-hidden />
								<span className="truncate font-mono">{abbreviateAddress(taproot)}</span>
							</div>
						</div>
					</div>
					<div className="my-1 h-px bg-border" role="separator" />
					<Link href="/wallet" className={MENU_ITEM} onClick={closeMenu}>
						<WalletIcon className="size-4 text-muted-foreground" aria-hidden="true" />
						My wallet
					</Link>
					<button
						type="button"
						role="menuitem"
						data-testid="header-profile-edit"
						className={MENU_ITEM}
						onClick={() => {
							closeMenu()
							openProfileEditor()
						}}
					>
						<PenLine className="size-4 text-muted-foreground" aria-hidden="true" />
						Edit profile
					</button>
					<Link href="/settings" className={MENU_ITEM} onClick={closeMenu}>
						<Settings className="size-4 text-muted-foreground" aria-hidden="true" />
						Settings
					</Link>
					<button
						type="button"
						role="menuitem"
						className={`${MENU_ITEM} text-destructive`}
						onClick={(evt) => {
							closeMenu()
							handleSignOut(evt)
						}}
					>
						<LogOut className="size-4" aria-hidden="true" />
						Sign out
					</button>
				</PopoverContent>
			</Popover>
		</>
	)
}

export default HeaderProfile
